import { isNil } from 'lodash-es'
import moment from 'moment'
import { FC, Fragment } from 'react'
import { DateFormatMoment } from 'utils/date'
import { useI18n } from 'utils/localisation'

import { RouteQuote } from '../QuoteSheet/QuoteSheetOption'
import { BookingTimelineItem } from './BookingTimelineItem'

type MainRouteScheduleProps = {
  mainQuote?: RouteQuote
  hasPreQuote?: boolean
  hasPostQuote?: boolean
}

const getIcon = (vesselName?: string) => {
  if (!vesselName) {
    return 'ship'
  }

  const iconMap = {
    BARGE: 'barge',
    TRUCK: 'truck',
  }

  return iconMap[vesselName] ?? 'ship'
}

export const MainRouteSchedule: FC<MainRouteScheduleProps> = ({
  mainQuote,
  hasPreQuote = false,
  hasPostQuote = false,
}) => {
  const { translate } = useI18n()

  if (isNil(mainQuote) || isNil(mainQuote.route.routeSchedule)) {
    return null
  }

  const { oceanLegs } = mainQuote.route.routeSchedule

  return (
    <>
      <BookingTimelineItem
        isFirstLeg={!hasPreQuote}
        isLastLeg={oceanLegs.length === 0 && !hasPostQuote}
        timelineContent={{
          timestamp: {
            label: translate('quote_sheet-option-transport_plan-ETD'),
            value: moment(mainQuote.route.etd).format(DateFormatMoment.DATE),
          },
          description: {
            label: translate('quote_sheet-option-transport_plan-POL'),
            value: mainQuote.route.routeSchedule?.pol?.name ?? '---',
          },
        }}
      />
      {oceanLegs.map((leg, i) => {
        const isLastLeg = i === oceanLegs.length - 1

        // The POD of the last leg we get from Big Schedule is not always equal to the final POD
        // e.g last leg POD is "DEWVN" while the final POD is "DEHAM"
        // -> There's an "invisible" leg from DEWVN to DEHAM carried out by truck or barge or whatever (not provided by carrier)
        // In this case, display whatever pod_id is on the last leg. Else, display the port name

        const legPodName: string = leg.pod?.name ?? leg.pod_id
        const portName: string = isLastLeg && leg.pod?._id === leg.pod_id ? leg.pod.name : legPodName
        const icon = getIcon(leg.vessel.name)

        return (
          <Fragment key={i}>
            <BookingTimelineItem
              icon={icon}
              timelineContent={{
                description: {
                  label: translate('quote_sheet-option-transport_plan-vessel'),
                  value: leg.vessel.name ?? '---',
                },
              }}
            />
            <BookingTimelineItem
              isLastLeg={!hasPostQuote && isLastLeg}
              timelineContent={{
                timestamp: {
                  label: translate('quote_sheet-option-transport_plan-ETA'),
                  value: moment(leg.eta).format(DateFormatMoment.DATE),
                },
                description: {
                  label: !isLastLeg
                    ? translate('quote_sheet-option-transport_plan-transshipment_port')
                    : translate('quote_sheet-option-transport_plan-port_of_discharge'),
                  value: portName,
                },
              }}
            />
          </Fragment>
        )
      })}
    </>
  )
}
